import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { SwiperSlide } from 'swiper/react';
import type { StyleProduct } from '@storefront/lib-global/codegen/types';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import CardSelector from '../CardSelector/CardSelector';
import { ColourSelectorImage, RadioGroup } from './ColourSelector.styles';

export type ColourSelectorProps = {
  colors: StyleProduct[];
  productKey: string;
  slug: string;
  setProductColor: Dispatch<SetStateAction<string>>;
  name: string;
};

export function ColourSelector({ colors, slug, productKey, setProductColor, name }: ColourSelectorProps) {
  const { locale } = useLocaleContext();
  const { t } = useTranslation(['lib-products-common']);

  const activeColor = colors?.find((color) => color.key === productKey);
  const activeIndex = colors?.indexOf(activeColor as StyleProduct);
  const [selectedCard, setSelectedCard] = useState(activeColor);
  const swiperRef = useRef<SwiperType>();

  useEffect(() => {
    setSelectedCard(activeColor);
  }, [activeColor]);

  useEffect(() => {
    swiperRef?.current?.slideTo(activeIndex ?? 0);
  }, [activeIndex]);

  const handleClick = (color: StyleProduct) => () => setSelectedCard(color);

  useEffect(() => {
    setProductColor(selectedCard?.color as string);
  }, [selectedCard, setProductColor]);

  return (
    <RadioGroup>
      <CardSelector
        headerText={`${t('pdp.colours.available', { count: colors?.length })}: ${selectedCard?.color}`}
        colors={colors}
        swiperRef={swiperRef}
      >
        {colors?.map((color, index) => (
          <SwiperSlide key={color.key}>
            <ColourSelectorImage
              onClick={handleClick(color as StyleProduct)}
              $isSelected={selectedCard?.key === color.key}
              className="colour-selector-slide"
              data-testid="colourSelectorItem"
            >
              <Link href={`/${slug}/${color.key}-p`} locale={locale} passHref>
                <a
                  role="radio"
                  aria-checked={selectedCard?.key === color.key}
                  title={color.color || color.key}
                  aria-label={`${t('pdp.colours.selection', {
                    color: color.color || color.key,
                    productName: name,
                    index: index + 1,
                    totalColors: colors.length,
                  })}`}
                  aria-current={selectedCard?.key === color.key}
                >
                  <ImagePartial
                    src={color.image || ''}
                    height={75}
                    width={75}
                    altText={color.color || ''}
                    aria-hidden
                  />
                </a>
              </Link>
              <div />
            </ColourSelectorImage>
          </SwiperSlide>
        ))}
      </CardSelector>
    </RadioGroup>
  );
}

export default ColourSelector;
